import React, { useState, useEffect } from 'react';
import { Printer, Wifi, Check, X, AlertCircle, RefreshCw } from 'lucide-react';
import receiptService from '../../services/receiptService';
import PageContainer from '../layout/PageContainer';
import { getOperationMode } from '../../hooks/useOperationMode';


const PrinterSettings: React.FC = () => {
  const [printerStatus, setPrinterStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [printerIP, setPrinterIP] = useState('192.168.0.188');
  const [printerPort, setPrinterPort] = useState('9100');
  const [printerType, setPrinterType] = useState<'network' | 'usb' | 'windows'>('windows');
  const [printerModel, setPrinterModel] = useState<'star' | 'epson'>('star');
  const [isTestPrinting, setIsTestPrinting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [operationMode, setOperationMode] = useState<'normal' | 'event'>('normal');

  useEffect(() => {
    // 操作モードを読み込み
    const currentMode = getOperationMode();
    setOperationMode(currentMode);

    // 設定をローカルストレージから読み込み
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('printerSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setPrinterIP(settings.ip || '192.168.0.188');
        setPrinterPort(settings.port || '9100');
        setPrinterType(settings.type || 'network');
        setPrinterModel(settings.model || 'star');
        console.log('✅ プリンター設定を読み込み:', settings);
      }
    };

    loadSettings();
    checkPrinterStatus();

    // 出張モード切り替えイベントリスナー
    const handleOperationModeChange = (event: CustomEvent) => {
      const newMode = event.detail.mode;
      console.log('🔄 出張モード切り替え検知: プリンター設定を再読み込み');
      setOperationMode(newMode);
      loadSettings();
      checkPrinterStatus();
    };

    window.addEventListener('operationModeChanged', handleOperationModeChange as EventListener);

    return () => {
      window.removeEventListener('operationModeChanged', handleOperationModeChange as EventListener);
    };
  }, []);

  const checkPrinterStatus = async () => {
    setPrinterStatus('checking');
    try {
      const status = await receiptService.checkPrinterStatus(printerModel, printerIP, printerPort, printerType);
      setPrinterStatus(status.status === 'online' ? 'online' : 'offline');
      // IPとポートはlocalStorageから読み込んだ値を維持（APIレスポンスで上書きしない）
    } catch (error) {
      setPrinterStatus('offline');
      setMessage({ type: 'error', text: 'プリンターステータスの確認に失敗しました' });
    }
  };

  const saveSettings = () => {
    const settings = {
      ip: printerIP,
      port: printerPort,
      type: printerType,
      model: printerModel
    };
    localStorage.setItem('printerSettings', JSON.stringify(settings));
    
    // 環境変数を更新（実際にはサーバー側で設定ファイルを更新する必要があります）
    setMessage({ type: 'success', text: '設定を保存しました' });
  };

  const testPrint = async () => {
    setIsTestPrinting(true);
    setMessage(null);
    
    try {
      const result = await receiptService.testPrint(printerType, printerModel, printerIP, printerPort);
      if (result.success) {
        setMessage({ type: 'success', text: 'テスト印刷が完了しました' });
      } else if (result.pdfBackup) {
        setMessage({ 
          type: 'info', 
          text: 'プリンターエラー。PDFバックアップが作成されました。' 
        });
      } else {
        setMessage({ type: 'error', text: result.error || 'テスト印刷に失敗しました' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsTestPrinting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Printer className="h-6 w-6" />
              プリンター設定
            </h2>
            {operationMode === 'event' && (
              <div className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium">
                🚚 出張モード
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* プリンターステータス */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  printerStatus === 'online' ? 'bg-green-100' :
                  printerStatus === 'offline' ? 'bg-red-100' :
                  'bg-yellow-100'
                }`}>
                  {printerStatus === 'online' ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : printerStatus === 'offline' ? (
                    <X className="h-5 w-5 text-red-600" />
                  ) : (
                    <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />
                  )}
                </div>
                <div>
                  <p className="font-medium">プリンターステータス</p>
                  <p className={`text-sm ${
                    printerStatus === 'online' ? 'text-green-600' :
                    printerStatus === 'offline' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {printerStatus === 'online' ? 'オンライン' :
                     printerStatus === 'offline' ? 'オフライン' :
                     '確認中...'}
                  </p>
                </div>
              </div>
              <button
                onClick={checkPrinterStatus}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                再確認
              </button>
            </div>
          </div>

          {/* プリンターモデル選択 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              プリンターモデル
            </label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => setPrinterModel('star')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  printerModel === 'star'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="text-sm font-medium">STAR</p>
                <p className="text-xs text-gray-500">TSP143III LAN</p>
              </button>
              <button
                onClick={() => setPrinterModel('epson')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  printerModel === 'epson'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="text-sm font-medium">EPSON</p>
                <p className="text-xs text-gray-500">TM-m30</p>
              </button>
            </div>
          </div>

          {/* プリンタータイプ選択 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              プリンタータイプ
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setPrinterType('network')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  printerType === 'network'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Wifi className="h-6 w-6 mx-auto mb-1" />
                <p className="text-sm">ネットワーク</p>
                <p className="text-xs text-gray-500">LAN接続</p>
              </button>
              <button
                onClick={() => setPrinterType('usb')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  printerType === 'usb'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Printer className="h-6 w-6 mx-auto mb-1" />
                <p className="text-sm">USB</p>
                <p className="text-xs text-gray-500">直接接続</p>
              </button>
              <button
                onClick={() => setPrinterType('windows')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  printerType === 'windows'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Printer className="h-6 w-6 mx-auto mb-1" />
                <p className="text-sm">Windows</p>
                <p className="text-xs text-gray-500">共有プリンター</p>
              </button>
            </div>
          </div>

          {/* ネットワーク設定 */}
          {printerType === 'network' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  プリンターIPアドレス
                </label>
                <input
                  type="text"
                  value={printerIP}
                  onChange={(e) => setPrinterIP(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="192.168.1.100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ポート番号
                </label>
                <input
                  type="text"
                  value={printerPort}
                  onChange={(e) => setPrinterPort(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="9100"
                />
              </div>
            </div>
          )}

          {/* メッセージ表示 */}
          {message && (
            <div className={`p-4 rounded-lg flex items-start gap-3 ${
              message.type === 'success' ? 'bg-green-50 text-green-800' :
              message.type === 'error' ? 'bg-red-50 text-red-800' :
              'bg-blue-50 text-blue-800'
            }`}>
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <p className="text-sm">{message.text}</p>
            </div>
          )}

          {/* アクションボタン */}
          <div className="flex gap-3">
            <button
              onClick={testPrint}
              disabled={isTestPrinting}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isTestPrinting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  テスト印刷中...
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4" />
                  テスト印刷
                </>
              )}
            </button>
            <button
              onClick={saveSettings}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              設定を保存
            </button>
          </div>

          {/* 注意事項 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-medium text-yellow-800 mb-2">設定時の注意事項</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              {printerModel === 'star' ? (
                <>
                  <li>• STAR TSP143III LANは事前にネットワーク設定が必要です</li>
                  <li>• プリンターユーティリティでIPアドレスを確認してください</li>
                </>
              ) : (
                <>
                  <li>• EPSON TM-m30は事前にネットワーク設定が必要です</li>
                  <li>• EpsonNet ConfigでIPアドレスを確認してください</li>
                </>
              )}
              <li>• プリンターとPCが同じネットワークに接続されている必要があります</li>
              <li>• ファイアウォールでポート9100を開放してください</li>
              <li>• プリンターエラー時は自動的にPDFバックアップが作成されます</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterSettings;